import { get } from 'lodash';
import { useMemo } from 'react';
import type { FieldPath } from 'react-hook-form';
import type { AgentProfileFields } from './types';
import { useAgentProfile } from './useAgentProfile';

export const AGENT_SYNC_PROPS = [
  { from: 'firstName', to: 'firstName' },
  { from: 'lastName', to: 'lastName' },
  { from: 'email', to: 'email' },
  { from: 'cellPhone', to: 'cellPhone' },
  { from: 'licenseNumber', to: 'licenseNumber' },
  { from: 'companyName', to: 'brokerage.companyName' },
  { from: 'brokerageLicenseNumber', to: 'brokerage.licenseNumber' },
  { from: 'brokerageOfficeEmail', to: 'brokerage.officeEmail' },
  { from: 'brokerageCellPhone', to: 'brokerage.cellPhone' },
  { from: 'brokerageEmail', to: 'brokerage.email' },
  { from: 'brokerageMlsCode', to: 'brokerage.mlsCode' },
  { from: 'broker.firstName', to: 'broker.firstName' },
  { from: 'broker.lastName', to: 'broker.lastName' },
  { from: 'broker.licenseNumber', to: 'broker.licenseNumber' },
  { from: 'broker.cellPhone', to: 'broker.cellPhone' },
  { from: 'broker.email', to: 'broker.email' },
  { from: 'supervisingBroker.firstName', to: 'supervisingBroker.firstName' },
  { from: 'supervisingBroker.lastName', to: 'supervisingBroker.lastName' },
  {
    from: 'supervisingBroker.licenseNumber',
    to: 'supervisingBroker.licenseNumber',
  },
  { from: 'supervisingBroker.cellPhone', to: 'supervisingBroker.cellPhone' },
  { from: 'supervisingBroker.email', to: 'supervisingBroker.email' },
  { from: 'officeManager.firstName', to: 'officeManager.firstName' },
  { from: 'officeManager.lastName', to: 'officeManager.lastName' },
  { from: 'officeManager.licenseNumber', to: 'officeManager.licenseNumber' },
  { from: 'officeManager.cellPhone', to: 'officeManager.cellPhone' },
  { from: 'officeManager.email', to: 'officeManager.email' },
  { from: 'street1', to: 'brokerage.street1' },
  { from: 'street2', to: 'brokerage.street2' },
  { from: 'city', to: 'brokerage.city' },
  { from: 'state', to: 'brokerage.state' },
  { from: 'zipCode', to: 'brokerage.zip' },
] as const satisfies readonly {
  from: FieldPath<AgentProfileFields>;
  to: string;
}[];

type ChangedField = {
  currentValue: string;
  newValue: string;
  propName: (typeof AGENT_SYNC_PROPS)[number]['to'];
};

// Fetches canonical Identity data for an agent and returns the fields that
// diverge from its current form values. Callers pass resolved identity UUIDs —
// use `useResolveUserIdentityId` / `useResolveTeamIdentityId` when the agent
// may hold a legacy Firebase ID.
export function useAgentProfileDiff(
  agent: object | undefined,
  userIdentityId: string | null | undefined,
  teamIdentityId: string | null | undefined,
  options?: {
    /** See {@link useAgentProfile} — opt in only for own/primary agents. */
    loadPreferences?: boolean;
  },
): ChangedField[] {
  const { data: identity = null } = useAgentProfile({
    userId: userIdentityId,
    teamId: teamIdentityId,
    loadPreferences: options?.loadPreferences,
  });

  return useMemo(
    () =>
      AGENT_SYNC_PROPS.map(({ from: sourcePath, to: propName }) => ({
        currentValue: get(agent, propName, ''),
        newValue: get(identity, sourcePath, ''),
        propName,
      })).filter(
        ({ newValue, currentValue }) => newValue && currentValue !== newValue,
      ),
    [agent, identity],
  );
}
